import { ChangeDetectionStrategy, Component, computed, inject, OnInit, signal } from '@angular/core';
import { DIALOG_DATA, DialogRef } from '@angular/cdk/dialog';
import { takeUntilDestroyed, toObservable, toSignal } from '@angular/core/rxjs-interop';
import { catchError, EMPTY, filter, from, switchMap } from 'rxjs';
import { RedemptionPaymentMethod, RedemptionView } from '../common';
import { RedemptionMethodsComponent } from './redemption-methods/redemption-methods.component.js';
import { CryptoRedemptionComponent } from './crypto-redemption/crypto-redemption.component.js';
import { CryptoConfirmRedemptionComponent } from './crypto-confirm-redemption/crypto-confirm-redemption.component.js';
import { CoinflowConfirmRedemptionComponent } from './coinflow-confirm-redemption/coinflow-confirm-redemption.component.js';
import { CoinflowRedemptionComponent } from './coinflow-redemption/coinflow-redemption.component.js';
import { VenmoRedemptionComponent } from './venmo-redemption/venmo-redemption.component.js';
import { CoinflowService } from '../../../core/coinflow.js';
import { LOGGER_FACTORY } from '../../../core/logger/logger-factory.provider';
import { MetadataViewService } from '../../../core/view-states/metadata-view.service.js';
import { ActiveRedemptionViewService } from '../../../core/view-states/active-redemption-view.service.js';
import { CasinoSettingKeyId } from '@gains-com/shared';
import { UserViewService } from '../../../core/view-states/user-view.service.js';
import { LoadingComponent } from '@gains-com/ui-angular';
import { PaymentMethodsViewService } from '../../../core/view-states/payment-methods-view.service.js';
import { AccountLimitsViewService } from '../../../core/view-states/account-limits-view.service.js';

export type RedemptionDialogData = {};

@Component({
  selector: 'app-redemptions-dialog',
  imports: [
    RedemptionMethodsComponent,
    CryptoRedemptionComponent,
    CryptoConfirmRedemptionComponent,
    CoinflowConfirmRedemptionComponent,
    CoinflowRedemptionComponent,
    VenmoRedemptionComponent,
    LoadingComponent,
  ],
  templateUrl: './redemptions-dialog.component.html',
  styleUrl: './redemptions-dialog.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class RedemptionsDialogComponent implements OnInit {
  // Service Injections
  private readonly _coinflow = inject(CoinflowService);
  private readonly _logger = inject(LOGGER_FACTORY).create('RedemptionsDialog');
  private readonly _metadataView = inject(MetadataViewService);
  private readonly _activeRedemptionViewService = inject(ActiveRedemptionViewService);
  private readonly _paymentMethodsViewService = inject(PaymentMethodsViewService);
  private readonly _userView = inject(UserViewService);
  private readonly _accountLimitsView = inject(AccountLimitsViewService);
  dialogRef = inject(DialogRef);
  data: RedemptionDialogData = inject(DIALOG_DATA);

  // Observable to Signal Conversions
  redemption = toSignal(this._activeRedemptionViewService.activeRedemption$);
  fiatRedemptionsEnabledPlatform = toSignal(
    this._metadataView.casinoSettingValue$<boolean>(CasinoSettingKeyId.RedemptionsFiat),
  );
  userState = toSignal(this._userView.state$);

  // Computed Signals
  fiatRedemptionsEnabledUser = computed(() => this.userState()?.country_id === 'USA');
  fiatRedemptionsEnabled = computed(() => this.fiatRedemptionsEnabledPlatform() && this.fiatRedemptionsEnabledUser());

  // Reactive Signals
  view = signal<RedemptionView>('redemption-methods');
  redemptionAmount = signal<number>(0);
  coinflowToken = signal<string | null>(null);

  async ngOnInit() {
    await Promise.all([
      this._userView.syncUserFeatures(),
      this._metadataView.syncCasinoSettings([CasinoSettingKeyId.RedemptionsFiat, CasinoSettingKeyId.RedemptionsCrypto]),
      this._accountLimitsView.fetchDailyRedemptionLimits(),
      this._paymentMethodsViewService.syncPaymentMethods(),
    ]);
  }

  constructor() {
    // Fetch the Coinflow session token once fiat redemptions are enabled.
    // Gated on `!coinflowToken()` so the trigger closes after the first set;
    // switchMap keeps the latest in-flight request.
    toObservable(computed(() => !this.coinflowToken() && this.fiatRedemptionsEnabled()))
      .pipe(
        filter((shouldFetch) => !!shouldFetch),
        switchMap(() =>
          from(this._coinflow.createSessionToken('bank_linking')).pipe(
            catchError((err: any) => {
              this._logger.error(`Coinflow createSessionToken() error: ${err.message}`);
              return EMPTY;
            }),
          ),
        ),
        takeUntilDestroyed(),
      )
      .subscribe((token) => this.coinflowToken.set(token));
  }

  close(result?: 'contact-support' | 'additional-verification' | null) {
    this._activeRedemptionViewService.clearActiveRedemptionPayment();
    this.dialogRef.close(result);
  }

  setRedemptionMethod({ amount, redemptionMethod }: { amount: number; redemptionMethod: RedemptionPaymentMethod }) {
    this.redemptionAmount.set(amount);

    switch (redemptionMethod) {
      case 'card':
      case 'standard':
        this.view.set('coinflow-redemption');
        break;
      case 'crypto':
        this.view.set('crypto-redemption');
        break;
      case 'venmo':
        this.view.set('venmo-redemption');
        break;
    }
  }
}
